import { AddOutlined, DeleteOutlineOutlined } from '@mui/icons-material';
import {
	Button,
	DatePicker,
	FormField,
	FormInput,
	FormSelect,
	type FormSelectOption,
} from '@practium/ui';
import { mixtureUnitFor, type useMixtureComposer } from '../../hooks/useMixtureComposer';
import type { MixtureUsage } from '../../utils/mixturesMapper';
import { MixtureBoxLimits } from './MixtureBoxLimits';
import styles from './MixtureComposer.module.css';

interface MixtureComposerProps {
	composer: ReturnType<typeof useMixtureComposer>;
	usage: MixtureUsage;
	vbox: number;
	productOptions: FormSelectOption[];
	comboProductOptions: FormSelectOption[];
	isPending: boolean;
	onSubmit: () => void;
}

export const MixtureComposer = ({
	composer,
	usage,
	vbox,
	productOptions,
	comboProductOptions,
	isPending,
	onSubmit,
}: MixtureComposerProps) => {
	const isEditing = !!composer.editingId;

	return (
		<div className={styles.composer}>
			<div className={styles.editor}>
				<div className={styles.tools}>
					<button
						type="button"
						className={styles.tool}
						onClick={composer.addProduct}
						aria-label="Adicionar produto"
					>
						<AddOutlined />
					</button>
				</div>
				{composer.isEmpty ? (
					<div className={styles.empty}>Nenhum produto na mistura. Use o + para começar</div>
				) : (
					<div className={styles.fields}>
						{composer.rows.map((row) => (
							<div key={row.key} className={styles.productRow}>
								<FormField label="Produto" error={composer.errors[`productKey-${row.key}`]}>
									<FormSelect
										value={row.productKey}
										onChange={(value) => composer.updateRow(row.key, 'productKey', value)}
										options={row.locked ? comboProductOptions : productOptions}
										placeholder="Selecione o produto"
										searchable
										hasError={!!composer.errors[`productKey-${row.key}`]}
										disabled={!!row.locked}
									/>
								</FormField>
								<FormField label="Dose" error={composer.errors[`dose-${row.key}`]}>
									<FormInput
										type="number"
										min="0"
										step="0.01"
										value={row.dose}
										placeholder="Informe a dose"
										onChange={(event) => composer.updateRow(row.key, 'dose', event.target.value)}
										suffix={mixtureUnitFor(row.productKey)}
										hasError={!!composer.errors[`dose-${row.key}`]}
										disabled={!!row.locked}
									/>
								</FormField>
								<button
									type="button"
									className={styles.trash}
									onClick={() => composer.removeProduct(row.key)}
									aria-label="Remover produto"
								>
									<DeleteOutlineOutlined />
								</button>
							</div>
						))}
						{composer.date !== null && (
							<div className={styles.dateRow}>
								<FormField label="Data" error={composer.errors.date}>
									<DatePicker
										value={composer.date}
										onChange={composer.setDate}
										hasError={!!composer.errors.date}
									/>
								</FormField>
							</div>
						)}
						{composer.errors.products && (
							<span className={styles.error}>{composer.errors.products}</span>
						)}
					</div>
				)}
			</div>
			<div className={styles.side}>
				<MixtureBoxLimits usage={usage} vbox={vbox} productCount={composer.products.length} />
				<Button
					variant="primary"
					icon={isEditing ? undefined : <AddOutlined />}
					onClick={onSubmit}
					isLoading={isPending}
					disabled={composer.isEmpty}
					className={styles.submit}
				>
					{isEditing ? 'Salvar Mistura' : 'Inserir Mistura'}
				</Button>
			</div>
		</div>
	);
};
